/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { ChatMessage as ChatMessageType } from '../../hooks/useChatbot';
import { SelectListItem } from 'apis/chatbot/Chatbot';

function decodeHtmlEntities(str: string): string {
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
}

function formatTime(date: Date): string {
  const h = date.getHours();
  const m = date.getMinutes();
  const period = h < 12 ? '오전' : '오후';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${period} ${hour12}:${String(m).padStart(2, '0')}`;
}

/** 💡 "질문내용" 패턴에서 질문 텍스트를 추출 */
function extractExamples(content: string): string[] {
  const regex = /💡\s*"([^"]+)"/g;
  const results: string[] = [];
  let match = regex.exec(content);
  while (match) {
    results.push(match[1]);
    match = regex.exec(content);
  }
  return results;
}

interface Props {
  message: ChatMessageType;
  onSendExample?: (text: string) => void;
  onSelectMatrix?: (text: string) => void;
}

export default function ChatMessage({ message, onSendExample, onSelectMatrix }: Props) {
  const isUser = message.role === 'user';
  const isWelcome = message.id === 'welcome';
  const [copied, setCopied] = useState(false);
  const [hover, setHover] = useState(false);

  const examples = isWelcome ? extractExamples(message.content) : [];
  const welcomeIntro = isWelcome ? message.content.split('\n\n')[0] : '';

  /**
   * 응답 복사.
   *
   * navigator.clipboard는 **보안 컨텍스트(HTTPS 또는 localhost)에서만** 존재한다.
   * 사내 서버를 http로 띄우면 undefined가 되어 writeText 호출 자체가 TypeError로 터지고,
   * .then()만 달려 있으면 아무 일도 없는 것처럼 조용히 실패한다. 그래서 구식 API로
   * 대체 경로를 두고, 실패는 로그로 남긴다.
   */
  const handleCopy = async () => {
    const text = message.content;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        // 화면에 보이거나 스크롤이 튀지 않도록 숨겨서 붙였다 뗀다
        ta.style.position = 'fixed';
        ta.style.top = '-9999px';
        ta.setAttribute('readonly', '');
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('execCommand("copy") 실패');
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('[Chatbot] 응답 복사 실패:', e);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        px: 2,
        py: 0.8,
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* 역할 라벨 */}
      <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem', mb: 0.3 }}>
        {isUser ? '나' : 'AI 어시스턴트'}
      </Typography>

      {/* 말풍선 */}
      <Box sx={{ position: 'relative', maxWidth: '85%' }}>
        <Box
          sx={{
            bgcolor: isUser ? '#135678' : '#f0f4f8',
            color: isUser ? '#fff' : '#333',
            px: 2,
            py: 1.5,
            borderRadius: isUser ? '16px 0 16px 16px' : '0 16px 16px 16px',
            fontSize: '0.85rem',
            lineHeight: 1.6,
            wordBreak: 'break-word',
            /* Markdown 스타일 */
            '& p': { m: 0, mb: 0.5, '&:last-child': { mb: 0 } },
            '& ul, & ol': { m: 0, pl: 2.5 },
            '& li': { mb: 0.3 },
            '& code': {
              bgcolor: isUser ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)',
              px: 0.5,
              borderRadius: '3px',
              fontSize: '0.8rem',
              fontFamily: 'monospace',
            },
            '& pre': {
              bgcolor: isUser ? 'rgba(0,0,0,0.2)' : '#e8ecf0',
              p: 1,
              borderRadius: '6px',
              overflow: 'auto',
              '& code': { bgcolor: 'transparent', p: 0 },
            },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              fontSize: '0.8rem',
              my: 0.5,
            },
            '& th, & td': {
              border: '1px solid',
              borderColor: isUser ? 'rgba(255,255,255,0.3)' : '#d0d7de',
              px: 1,
              py: 0.3,
            },
            '& th': {
              bgcolor: isUser ? 'rgba(255,255,255,0.1)' : '#f5f5f5',
              fontWeight: 'bold',
            },
            '& strong': { fontWeight: 700 },
          }}
        >
          {isWelcome && examples.length > 0 ? (
            <>
              <Typography sx={{ fontSize: '0.85rem', mb: 1 }}>{welcomeIntro}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                {examples.map((ex) => (
                  <Chip
                    key={ex}
                    label={`💡 ${ex}`}
                    size="small"
                    onClick={() => onSendExample?.(ex)}
                    sx={{
                      justifyContent: 'flex-start',
                      height: 'auto',
                      py: 0.5,
                      px: 0.5,
                      fontSize: '0.8rem',
                      bgcolor: '#e3f2fd',
                      color: '#1565c0',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#bbdefb' },
                      '& .MuiChip-label': { whiteSpace: 'normal' },
                    }}
                  />
                ))}
              </Box>
            </>
          ) : isUser ? (
            <Box sx={{ whiteSpace: 'pre-wrap' }}>{message.content}</Box>
          ) : (
            <>
              <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                {message.content.replace(/\[/g, '\\[').replace(/\]/g, '\\]')}
              </ReactMarkdown>
              {message.selectItems && message.selectItems.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  {/* 그리드 테이블 */}
                  <Box
                    component="table"
                    sx={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '0.75rem',
                      mb: 1,
                      '& th, & td': {
                        border: '1px solid #d0d7de',
                        px: 0.8,
                        py: 0.4,
                        textAlign: 'left',
                      },
                      '& th': {
                        bgcolor: '#e8ecf0',
                        fontWeight: 700,
                        color: '#333',
                        whiteSpace: 'nowrap',
                      },
                      '& td': {
                        color: '#444',
                      },
                      '& tr:hover td': {
                        bgcolor: '#f5f9fb',
                      },
                    }}
                  >
                    <thead>
                      <tr>
                        <th>No</th>
                        <th>UT No</th>
                        <th>UT명</th>
                        <th>버전</th>
                        <th>상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {message.selectItems.map((item: SelectListItem, idx: number) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{item.utmNo || item.utmId}</td>
                          <td>{decodeHtmlEntities(item.utmNm)}</td>
                          <td>{item.verNo || '-'}</td>
                          <td>{item.utmWkProgStatCdNm || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                </Box>
              )}
            </>
          )}
          {message.isStreaming && (
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 6,
                height: 14,
                bgcolor: '#2D9BB2',
                ml: 0.3,
                animation: 'blink 1s infinite',
                verticalAlign: 'text-bottom',
                '@keyframes blink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0 },
                },
              }}
            />
          )}
        </Box>

        {/* 복사 버튼 (AI 응답만, welcome 제외) */}
        {!isUser && !isWelcome && hover && !message.isStreaming && (
          <Tooltip title={copied ? '복사됨' : '복사'}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                bgcolor: '#fff',
                border: '1px solid #e0e0e0',
                width: 26,
                height: 26,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: 14, color: '#4caf50' }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 14, color: '#666' }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* 전송 시간 */}
      <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.65rem', mt: 0.3 }}>
        {formatTime(message.timestamp)}
      </Typography>
    </Box>
  );
}
